import { grpc } from "@improbable-eng/grpc-web";
import React, { useEffect, useState } from "react";
import { Nav } from "../account/Nav";
import { getErrorDetail, idpac, idpc } from "../api";
import { AuthContext, User, User_Status } from "../generated/idp/api/idp";

function UserDetail(props: { user: User; onUserUpdate: (u: User) => void }): JSX.Element {
  const { user, onUserUpdate } = props;
  const [updating, setUpdating] = useState(false);

  const toggleEnhancedAuth = async () => {
    setUpdating(true);
    try {
      await idpc.SetUserEnhancedSecurity({
        id: user.id,
        enabled: !user.enhancedAuthentication,
      });
      onUserUpdate({
        ...user,
        enhancedAuthentication: !user.enhancedAuthentication,
      });
    } catch (err) {
      // TODO: show error to user
      console.error(err);
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div>
      <h2>User Details</h2>
      <p>ID: {user.id}</p>
      <p>External: {user.external ? "Yes" : "No"}</p>
      <p>Status: {User_Status[user.status]}</p>
      <p>
        Enhanced Authentication: {user.enhancedAuthentication ? "Yes" : "No"}{" "}
        <button onClick={toggleEnhancedAuth} disabled={updating}>
          {updating ? "..." : user.enhancedAuthentication ? "Disable" : "Enable"}
        </button>
      </p>
      <p>TOS Accepted ID: {user.tosIdAccepted}</p>
      {user.ghostSince && <p>Ghost Since: {user.ghostSince.toLocaleString()}</p>}

      <h3>Profile</h3>
      {user.profile && (
        <ul>
          {Object.entries(user.profile).map(([key, value]) => (
            <li key={key}>
              {key}: {value.toString()}
            </li>
          ))}
        </ul>
      )}

      <h3>TOTP Authenticators</h3>
      <ul>
        {(user.totpAuthenticator ?? []).map((auth) => (
          <li key={btoa(String.fromCharCode.apply(null, auth.id as any))}>{auth.name}</li>
        ))}
      </ul>

      <h3>WebAuthn Authenticators</h3>
      <ul>
        {(user.webauthnAuthenticator ?? []).map((auth) => (
          <li key={btoa(String.fromCharCode.apply(null, auth.id as any))}>{auth.name}</li>
        ))}
      </ul>

      <h3>Email Addresses</h3>
      <ul>
        {(user.emailAddress ?? []).map((email) => (
          <li key={btoa(String.fromCharCode.apply(null, email.id as any))}>
            {email.localPart}@{email.domain} ({email.trust})
          </li>
        ))}
      </ul>

      <h3>Phone Numbers</h3>
      <ul>
        {(user.phoneNumber ?? []).map((phone) => (
          <li key={btoa(String.fromCharCode.apply(null, phone.id as any))}>
            {/* Add phone number display here */}
          </li>
        ))}
      </ul>

      <h3>Member Of</h3>
      <ul>
        {(user.memberOf ?? []).map((group: string) => (
          <li key={group}>{group}</li>
        ))}
      </ul>

      <h3>Claims</h3>
      {user.claim && (
        <ul>
          {Object.entries(user.claim).map(([key, value]) => (
            <li key={key}>
              {key}: {JSON.stringify(value)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function Users(props: {
  onLogout: () => void;
  authenticate: (a: AuthContext) => void;
}): JSX.Element {
  const [users, setUsers] = useState<User[] | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const authenticate = props.authenticate;

  useEffect(() => {
    let ignore = false;
    const fetchData = async () => {
      try {
        const res = await idpac.ListUsers({});
        if (!ignore) {
          setUsers(res.user ?? null);
        }
      } catch (err) {
        if (err.code === grpc.Code.Unauthenticated) {
          let ia = getErrorDetail(err, AuthContext);
          if (ia !== null) {
            authenticate(ia);
          }
        }
      }
    };
    fetchData();
    return () => {
      ignore = true;
    };
  }, [authenticate]);

  return (
    <Nav>
      <table>
        <thead>
          <tr>
            <th>ID</th>
            <th>Name</th>
            <th>Aktiv</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {(users ?? []).map((u) => (
            <tr key={u.id}>
              <td>{u.id}</td>
              <td>{u.profile?.name}</td>
              <td>{u.status === User_Status.OK ? "☑" : "☒"}</td>
              <td>
                <button onClick={() => setSelectedUser(u)}>Details</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {selectedUser && (
        <Modal onClose={() => setSelectedUser(null)}>
          <UserDetail user={selectedUser} onUserUpdate={setSelectedUser} />
        </Modal>
      )}
    </Nav>
  );
}

const modalStyle: React.CSSProperties = {
  position: "fixed",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  backgroundColor: "#fff",
  padding: "50px",
  zIndex: 1000,
  borderRadius: "10px",
  boxShadow: "0 5px 15px rgba(0,0,0,.5)",
};

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0, 0, 0, .7)",
  zIndex: 1000,
};

export function Modal(props: { children: React.ReactNode; onClose: () => void }): JSX.Element {
  return (
    <>
      <div style={overlayStyle} onClick={props.onClose} />
      <div style={modalStyle}>
        {props.children}
        <button onClick={props.onClose}>Close</button>
      </div>
    </>
  );
}
